import { useContext, useEffect, useMemo, useState } from 'react';

import { Visibility } from '@mui/icons-material';
import { Box, Chip, IconButton, ThemeProvider, Tooltip } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import * as FiscalXmlObservacaoAPI from '../../../api/fiscalXmlObservacao';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../hooks/useMRTState';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';

import ObservacaoFiscalDialog from './ObservacaoFiscalDialog';

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function formatarStatus(value) {
    const labels = {
        PENDENTE_REVISAO: 'Pendente de revisão',
        PENDENTE_VINCULO: 'Pendente de vínculo',
        APLICADA: 'Aplicada',
        IGNORADA: 'Ignorada',
    };

    return labels[value] || value || '';
}

function corStatus(value) {
    const cores = {
        PENDENTE_REVISAO: 'warning',
        PENDENTE_VINCULO: 'error',
        APLICADA: 'success',
        IGNORADA: 'default',
    };

    return cores[value] || 'default';
}

function formatarClassificacao(value) {
    const labels = {
        CANDIDATA_REVISAO: 'Candidata à revisão',
        SOMENTE_EVIDENCIA: 'Somente evidência',
    };

    return labels[value] || value || '';
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const urlParams = new URLSearchParams(window.location.search);

    const ul = urlParams.get('ul');
    const li = urlParams.get('li');

    const [token] = useState(localStorage.getItem('token') || '');

    const [dados, setDados] = useState([]);
    const [observacaoSelecionadaUuid, setObservacaoSelecionadaUuid] = useState(null);

    const mrt = useMRTState({
        storageKey: 'fiscal-xml-observacao.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [
                {
                    id: 'status',
                    value: 'PENDENTE_REVISAO',
                },
            ],
            globalFilter: '',
            sorting: [
                {
                    id: 'updatedAt',
                    desc: true,
                },
            ],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
    });

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await FiscalXmlObservacaoAPI.listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            setDados(response.data.data || []);

            setRowCount(Number(response.data.meta?.totalRowCount) || 0);

            setIsError(false);
        } catch (error) {
            setIsError(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as observações fiscais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', {
                replace: true,
            });

            return;
        }

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) {
            return;
        }

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    const columns = useMemo(
        () => [
            {
                id: 'observacoesFiscais',
                header: 'OBSERVAÇÕES FISCAIS DO XML',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'status',
                        header: 'Situação',
                        filterVariant: 'select',
                        filterSelectOptions: [
                            {
                                text: 'Pendente de revisão',
                                value: 'PENDENTE_REVISAO',
                            },
                            {
                                text: 'Pendente de vínculo',
                                value: 'PENDENTE_VINCULO',
                            },
                            {
                                text: 'Aplicada',
                                value: 'APLICADA',
                            },
                            {
                                text: 'Ignorada',
                                value: 'IGNORADA',
                            },
                        ],
                        Cell: ({ cell }) => (
                            <Chip size="small" label={formatarStatus(cell.getValue())} color={corStatus(cell.getValue())} />
                        ),
                    },
                    {
                        accessorKey: 'classificacaoFonte',
                        header: 'Classificação',
                        filterVariant: 'select',
                        filterSelectOptions: [
                            {
                                text: 'Candidata à revisão',
                                value: 'CANDIDATA_REVISAO',
                            },
                            {
                                text: 'Somente evidência',
                                value: 'SOMENTE_EVIDENCIA',
                            },
                        ],
                        Cell: ({ cell }) => formatarClassificacao(cell.getValue()),
                    },
                    {
                        accessorKey: 'chaveAcesso',
                        header: 'Chave de acesso',
                        size: 320,
                    },
                    {
                        accessorKey: 'numeroItem',
                        header: 'Item',
                        size: 70,
                    },
                    {
                        accessorKey: 'Produto.codigo',
                        header: 'Código',
                        size: 90,
                    },
                    {
                        accessorKey: 'Produto.descricao',
                        header: 'Produto',
                    },
                    {
                        accessorKey: 'Fornecedor.descricao',
                        header: 'Fornecedor',
                    },
                    {
                        accessorKey: 'codigoProdutoFornecedor',
                        header: 'Código no fornecedor',
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderRowActions={({ row }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                            }}
                        >
                            <Tooltip title="Visualizar observação fiscal">
                                <IconButton color="primary" onClick={() => setObservacaoSelecionadaUuid(row.original.uuid)}>
                                    <Visibility />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>

            <ObservacaoFiscalDialog
                open={Boolean(observacaoSelecionadaUuid)}
                uuid={observacaoSelecionadaUuid}
                onClose={() => setObservacaoSelecionadaUuid(null)}
                onAtualizado={fetchData}
            />
        </>
    );
}

export default Index;
